import { useState } from 'react';
import {
  Box,
  Button,
  Radio,
  RadioGroup,
  Checkbox,
  FormControlLabel,
  Typography,
  LinearProgress,
  Link,
  TextField,
  Collapse,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import { toast } from 'react-toastify';
import FAST_ICON from '@bumble/app/assets/fast.png';
import OOKLA_ICON from '@bumble/app/assets/ookla.png';

interface RouterRunSpeedTestProps {
  cpeId: string;
  setIntervalId: (id: number | null) => void;
  privateLine?: boolean;
}

type InterfaceType = 'wan0' | 'wan1' | 'cellular';

export const RouterRunSpeedTest = ({ cpeId, setIntervalId, privateLine }: RouterRunSpeedTestProps) => {
  const api = useApi();
  const [selected, setSelected] = useState<'fast.com' | 'ookla'>('ookla');
  const [isAdvancedChecked, setIsAdvancedChecked] = useState<boolean>(false);
  const [ooklaServerOption, setOoklaServerOption] = useState<'nearest' | 'specific'>('nearest');
  const [intf, setIntf] = useState<InterfaceType>(privateLine ? 'cellular' : 'wan0');
  const [serverId, setServerId] = useState<string>('');
  const [downloadSpeed, setDownloadSpeed] = useState<number>();
  const [uploadSpeed, setUploadSpeed] = useState<number>();
  const [pingLatency, setPingLatency] = useState<number>();
  const [pingJitter, setPingJitter] = useState<number>();
  const [resultUrl, setResultUrl] = useState<string>();
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [hasResult, setHasResult] = useState<boolean>(false);
  const [runId, setRunId] = useState(0);
  const [progress, setProgress] = useState(0);
  const [intervalId, setLocalIntervalId] = useState<number | null>(null);

  const step = 1200;
  const step_Invoke = 4800;
  const total = 120000;
  const totalStep = total / step;
  const STEP_PCT = 100 / totalStep;
  const stepsPerPoll = Math.round(step_Invoke / step);

  const startSpeedTest = useMutation(
    api.startCpeSpeedtestCliEndpointApiV1StartCpeSpeedtestCliPost,
    {
      onSuccess: (data: any) => {
        const msg = data?.data?.status || 'Starting test...';
        toast.success(msg);
        setHasResult(false);
        setIsRunning(true);
        setProgress(0);
        const startTime = Date.now();
        let hasCompleted = false;
        let count = 0;
        const id = window.setInterval(() => {
          const elapsed = Date.now() - startTime;
          count += 1;
          setProgress((p) => Math.min(100, Math.round((p + STEP_PCT) * 100) / 100));
          if (elapsed > total || count >= totalStep) {
            clearInterval(intervalId!);
            setLocalIntervalId(null);
            setIntervalId(null);
            setIsRunning(false);
            setProgress(0);
            toast.error('Speed test timed out');
            return;
          }
          if (count % stepsPerPoll !== 0) return;

          testResultRefetch()
            .then((res) => {
              const payload = res?.data?.data;
              if (!payload) return;

              if (!hasCompleted) {
                hasCompleted = true;
                clearInterval(id);
                setLocalIntervalId(null);
                setIntervalId(null);
                setIsRunning(false);
                setHasResult(true);
                setProgress(100);
                toast.success('Speed test completed');
              }

              if (payload.result_url) {
                setResultUrl(payload.result_url);
                setProgress(100);
                return;
              }
              setDownloadSpeed(payload.download_speed_mbps);
              setUploadSpeed(payload.upload_speed_mbps);
              setPingLatency(payload.ping_latency_ms);
              setPingJitter(payload.ping_jitter_ms);
            })
            .catch((err) => {
              if (err.status === 404) return;
              clearInterval(intervalId!);
              setLocalIntervalId(null);
              setIntervalId(null);
              setIsRunning(false);
              setProgress(0);
              apiError(err);
            });
        }, step);
        setLocalIntervalId(id);
        setIntervalId(id);
      },
      onError: (err: any) => {
        setIsRunning(false);
        setProgress(0);
        apiError(err);
      },
    }
  );

  const { refetch: testResultRefetch } = useQuery(
    ['gettestResultData', cpeId, selected, runId],
    api.getCpeSpeedtestCliTestResultApiV1GetCpeSpeedtestCliTestResultGet.bind(null, {
      cpe_id: cpeId,
      test_type: selected,
    }),
    {
      enabled: false,
      meta: { ignore404: true },
      retry: false,
      onError: (err: any) => {
        if (err.status === 404) return;
        if (err.status === 500) {
          clearInterval(intervalId!);
          setLocalIntervalId(null);
          setIntervalId(null);
          setIsRunning(false);
          setProgress(0);
          apiError(err);
          return;
        }
        apiError(err);
      },
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: Infinity,
    }
  );

  const handleGo = () => {
    setRunId((r) => r + 1);
    setHasResult(false);
    setProgress(0);
    setResultUrl(undefined);

    let apiParams: any = { cpe_id: cpeId, test_type: selected };
    if (selected === 'ookla') {
      apiParams.ookla_interface = intf;
      if (isAdvancedChecked) {
        if (ooklaServerOption === 'specific' && serverId.trim()) {
          apiParams.ookla_server_id = serverId.trim();
        } else {
          apiParams.ookla_enable_shortest_distance = true;
        }
      }
    }

    startSpeedTest.mutate(apiParams);
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <RadioGroup
        value={selected}
        onChange={(e) => {
          const newValue = e.target.value as 'fast.com' | 'ookla';
          setSelected(newValue);
          setOoklaServerOption('nearest');
          if (newValue !== 'ookla' && isAdvancedChecked) {
            setIsAdvancedChecked(false);
          }
        }}
        sx={{ display: 'flex', flexDirection: 'column', ml: 4 }}
      >
        <FormControlLabel
          value="ookla"
          control={<Radio sx={{ alignSelf: 'flex-start', mt: 0.5 }} />}
          label={
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              textAlign: 'center',
            }}>
              <Box
                component="img"
                src={OOKLA_ICON}
                alt="Ookla logo"
                loading="lazy"
                sx={{ width: 40, height: 40, objectFit: 'contain' }}
              />
              <Typography component="span">Ookla Speedtest</Typography>
            </Box>
          }
          sx={{
            px: 1,
            py: 0.5,
            borderRadius: 1,
            alignItems: 'center',
            gap: 2
          }}
        />
        {selected === 'ookla' && (
          <Box>
            <RadioGroup
              value={intf}
              onChange={(e) => {
                const newValue = e.target.value as InterfaceType;
                setIntf(newValue);
              }}
              sx={{ display: 'flex', flexDirection: 'row', ml: 7 }}
            >
              {!privateLine && <>
              <FormControlLabel
                value="wan0"
                control={<Radio />}
                label="Primary WAN"
                sx={{
                  mr: 5,
                  mt: 6,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  alignItems: 'center',
                }}
              />
              <FormControlLabel
                value="wan1"
                control={<Radio />}
                label="Secondary WAN"
                sx={{ mt: 6, mr: 5 }}
              />
              </>}
              <FormControlLabel
                value="cellular"
                control={<Radio />}
                label="Cellular"
                sx={{ mt: 6 }}
              />
            </RadioGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isAdvancedChecked}
                  onChange={(e) => {
                    if (e.target.checked === false) {
                      setOoklaServerOption('nearest');
                    }
                    setIsAdvancedChecked(e.target.checked)
                  }}
                  sx={{ alignSelf: 'flex-start', mt: 0.5 }}
                />
              }
              label='Advanced'
              sx={{
                mt: 4,
                ml: 4,
                px: 1,
                py: 0.5,
                borderRadius: 1,
                alignItems: 'center',
              }}
            />
            <Collapse in={isAdvancedChecked}>
            <Box sx={{ ml: 10, mt: 1, mb: 2, p: 2 }}>
              <RadioGroup
                value={ooklaServerOption}
                onChange={(e) => {
                  setOoklaServerOption(e.target.value as 'nearest' | 'specific');
                  if (e.target.value === 'nearest') {
                    setServerId('');
                  }
                }}
              >
                <FormControlLabel
                  value="nearest"
                  control={<Radio />}
                  label="Use the Nearest Ookla Speed Test Server"
                />
                <Box>
                  <FormControlLabel
                    value="specific"
                    control={<Radio />}
                    label="Use a Specific Ookla Speed Test Server"
                  />
                  {ooklaServerOption === 'specific' && (
                    <Box sx={{ ml: 9, mt: 1 }}>
                      <TextField
                        size="small"
                        label="Server ID"
                        value={serverId}
                        onChange={(e) => setServerId(e.target.value)}
                        sx={{ width: 200 }}
                      />
                    </Box>
                  )}
                </Box>
              </RadioGroup>
            </Box>
          </Collapse>
          </Box>
        )}
        <FormControlLabel
          value="fast.com"
          control={<Radio sx={{ alignSelf: 'flex-start', mt: 0.5 }} />}
          label={
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              textAlign: 'center',
            }}>
              <Box
                component="img"
                src={FAST_ICON}
                alt="fast.com logo"
                loading="lazy"
                sx={{ width: 40, height: 40, objectFit: 'contain' }}
              />
              <Typography component="span">fast.com</Typography>
            </Box>
          }
          sx={{
            mr: 3,
            mt: 6,
            px: 1,
            py: 0.5,
            borderRadius: 1,
            gap: 2,
            alignItems: 'center',
          }}
        />
      </RadioGroup>
      {isRunning && (
        <Box display="flex" alignItems="center" gap={2}>
          <Box flex={1}>
            <LinearProgress
              variant="determinate"
              value={progress}
              color="info"
              sx={{ height: 10, borderRadius: 10 }}
            />
          </Box>
          <Typography variant="body2" width={48} textAlign="right">
            {progress}%
          </Typography>
        </Box>
      )}

      {hasResult && !resultUrl && (
        <Box sx={{ mt: 5 }}>
          {downloadSpeed !== -1 && downloadSpeed !== undefined && (
            <Typography variant="body1">
              Download Speed (Mbps): {downloadSpeed}
            </Typography>
          )}
          {uploadSpeed !== -1 && uploadSpeed !== undefined && (
            <Typography variant="body1">
              Upload Speed (Mbps): {uploadSpeed}
            </Typography>
          )}
          {pingLatency !== -1 && pingLatency !== undefined && (
            <Typography variant="body1">Ping Latency (ms): {pingLatency}</Typography>
          )}
          {pingJitter !== -1 && pingJitter !== undefined && (
            <Typography variant="body1">Ping Jitter (ms): {pingJitter}</Typography>
          )}
        </Box>
      )}
      {resultUrl && (
        <Box display="flex" justifyContent="center">
          <Link
            href={resultUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              fontWeight: 'bold'
            }}
          >
            View Result
          </Link>
        </Box>
      )}
      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="outlined"
          onClick={handleGo}
          disabled={startSpeedTest.isLoading || isRunning || (!serverId && ooklaServerOption  === 'specific')}
        >
          GO
        </Button>
      </Box>
    </Box>
  );
};
